import React, { useEffect, useMemo } from 'react';
import { connect } from 'react-redux';
import { push } from 'connected-react-router';
import { selectedSpotId, selectedSpotInfo } from 'selectors';
import { getPromoCodeReportsAction } from 'actions/actions';
import { AppState } from 'reducers/reducers';
import { DiscountCodeProvider } from 'contexts/DiscountCodeContext';
import { useOrganization } from 'contexts/OrganizationsContext';
import { isValidDateForReports } from 'utils/helpers';
import MonthYearUtil, { MonthYear } from 'utils/monthYear';
import { trackPageViewed } from 'utils/analytics';
import usePaginatedFiltersWithSearch from 'hooks/usePaginatedFiltersWithSearch';
import { nextSort, parseOrdering, toOrdering } from 'utils/ordering';
import { PROMO_CODE_SORT_COLUMNS } from 'constants/promoCodes';
import { PromoCodeSortColumn } from 'types';
import PromoCode from 'components/Dashboard/Modules/PromoCode';
import DiscountCodeReport from 'components/Dashboard/Modules/DiscountCodeReport/DiscountCodeReport';

type PromoCodeModuleContainerProps = ReturnType<typeof mapStateToProps> & typeof mapDispatchToProps;

function PromoCodeModuleContainer({
    selectedSpotId,
    selectedSpotInfo,
    getPromoCodeReports,
    spotPromoCodeReport,
    profile,
    push: connectedPush,
}: PromoCodeModuleContainerProps): JSX.Element {
    const monthYearUtil = new MonthYearUtil();

    const { organization } = useOrganization();

    const { filters, urlUpdater } = usePaginatedFiltersWithSearch<{
        showUsableCodes: string;
        monthYear: MonthYear['value'];
        ordering: string;
    }>({
        filterValidators: {
            showUsableCodes(current) {
                if (current === null) return 'true';
                return current === 'true' ? 'true' : 'false';
            },
            monthYear: function dateValidator(currentValue: string | null): string {
                return isValidDateForReports(currentValue) ? currentValue : monthYearUtil.currentMonthYear.value;
            },
            // Optional; an unrecognized value is treated as "no sort" (see parseOrdering below).
            ordering: (current) => current,
        },
        push: connectedPush,
    });

    const showingUsableCodes: boolean = useMemo(() => filters.showUsableCodes === 'true', [filters.showUsableCodes]);
    const pageNumber: number = useMemo(() => (filters.page ? Number(filters.page) : 1), [filters.page]);
    const sort = useMemo(() => parseOrdering(filters.ordering, PROMO_CODE_SORT_COLUMNS), [filters.ordering]);

    useEffect(() => {
        const [month, year] = filters.monthYear.split(':');
        getPromoCodeReports({ date: { month, year } });
    }, [selectedSpotId, getPromoCodeReports, filters.monthYear]);

    useEffect(() => {
        trackPageViewed({
            pageName: 'Promo Codes',
            organizationId: organization?.pk,
            spotId: selectedSpotId,
            affiliationRole: organization?.role,
            superuser: profile.superuser,
        });
    }, [
        profile.superuser,
        organization?.pk,
        organization?.role,
        selectedSpotId,
        filters.showUsableCodes,
        filters.page,
    ]);

    function toggleShowingUsableCodes() {
        urlUpdater.showUsableCodes(!showingUsableCodes ? 'true' : 'false');
        urlUpdater.page('1');
    }

    function handleSortChange(column: PromoCodeSortColumn) {
        urlUpdater.ordering(toOrdering(nextSort(sort, column)));
        urlUpdater.page('1');
    }

    return (
        <DiscountCodeProvider>
            <PromoCode
                selectedSpotInfo={selectedSpotInfo}
                showingUsableCodes={showingUsableCodes}
                page={pageNumber}
                toggleShowingUsableCodes={toggleShowingUsableCodes}
                handlePageChange={urlUpdater.page}
                searchTerm={filters.searchTerm}
                currentSearchTerm={filters.currentSearchTerm}
                setSearchTerm={urlUpdater.searchTerm}
                sort={sort}
                onSortChange={handleSortChange}
            />
            {spotPromoCodeReport && (
                <DiscountCodeReport
                    monthYear={filters.monthYear}
                    onMonthYearSelect={(mY) => urlUpdater.monthYear(mY)}
                    discountCodeReport={spotPromoCodeReport}
                    monthYearOptions={monthYearUtil.monthYearsForSelect}
                />
            )}
        </DiscountCodeProvider>
    );
}

function mapStateToProps(state: AppState) {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const spotId = selectedSpotId(state)!;
    return {
        selectedSpotId: spotId,
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        selectedSpotInfo: selectedSpotInfo(state)!,
        spotPromoCodeReport: state.reports.promoCodeReports[spotId],
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        profile: state.auth.profile!,
    };
}

const mapDispatchToProps = {
    getPromoCodeReports: getPromoCodeReportsAction,
    push: push,
};

export default connect(mapStateToProps, mapDispatchToProps)(PromoCodeModuleContainer);
